import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

const normalizeText = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

const normalizeRequirementNames = (rawItems) => {
  if (!Array.isArray(rawItems)) return [];
  return rawItems
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        return String(item.name || item.label || item.title || item.requiredName || '').trim();
      }
      return '';
    })
    .filter(Boolean);
};

const getTaskEntriesForCategory = (categoryData = {}) => {
  if (!categoryData || typeof categoryData !== 'object') return [];

  if (categoryData.workflow?.tasks && typeof categoryData.workflow.tasks === 'object') {
    return Object.entries(categoryData.workflow.tasks).filter(([, task]) => task && typeof task === 'object');
  }

  return Object.entries(categoryData).filter(([key, task]) => (
    key !== 'workflow' && task && typeof task === 'object'
  ));
};

const Documents = () => {
  const { currentUser, employeeContext } = useAuth();
  const { companyId, employeeId } = employeeContext || {};

  const [documents, setDocuments] = useState({ files: {}, required: [] });
  const [onboardingReqs, setOnboardingReqs] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState('');
  const [pendingRequirementUpload, setPendingRequirementUpload] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!companyId || !employeeId) return;

    const docsRef = ref(database, `companies/${companyId}/employees/${employeeId}/documents`);
    const unsubscribeDocs = onValue(docsRef, (snap) => {
      setDocuments(snap.val() || { files: {}, required: [] });
      setLoading(false);
    });

    // Also fetch onboarding tasks to find document requirements
    const tasksRef = ref(database, `companies/${companyId}/onboarding/${employeeId}/tasks`);
    const unsubscribeTasks = onValue(tasksRef, (snap) => {
      const data = snap.val() || {};
      const reqs = [];
      Object.values(data).forEach((category) => {
        const taskEntries = getTaskEntriesForCategory(category);
        taskEntries.forEach(([, task]) => {
          const requiredDocs = normalizeRequirementNames(task?.requiredDocuments || task?.documentTypes || []);
          if (requiredDocs.length > 0) {
            reqs.push(...requiredDocs);
            return;
          }

          const title = String(task?.title || '').trim();
          const lowerTitle = title.toLowerCase();
          if (
            task?.type === 'document' ||
            task?.automationType === 'document_request' ||
            lowerTitle.includes('upload') ||
            lowerTitle.includes('document')
          ) {
            reqs.push(title || 'Document Upload');
          }
        });
      });
      const deduped = [];
      const seen = new Set();
      reqs.forEach((name) => {
        const norm = normalizeText(name);
        if (!norm || seen.has(norm)) return;
        seen.add(norm);
        deduped.push(name);
      });
      setOnboardingReqs(deduped);
    });

    return () => {
        unsubscribeDocs();
        unsubscribeTasks();
    };
  }, [companyId, employeeId]);

  const handleFileUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setPendingRequirementUpload('');
      return;
    }

    if (!currentUser?.uid || !currentUser?.email) {
      alert('You must be logged in to upload documents.');
      return;
    }

    try {
      setUploading(true);
      const requirementName = (pendingRequirementUpload || selectedRequirement || '').trim();
      
      // Upload via secure backend endpoint (server handles encryption)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('companyUID', companyId);
      formData.append('employeeId', employeeId);
      formData.append('docType', requirementName ? 'required_document' : 'other');
      if (requirementName) {
        formData.append('requestedDocumentName', requirementName);
      }
      formData.append('uploadedBy', currentUser?.email || 'Employee');
      formData.append('userUID', currentUser?.uid || '');
      formData.append('userEmail', currentUser?.email || '');
      formData.append('clientEncrypted', 'false');

      console.log('Uploading file to backend for encryption...');
      const response = await fetch(`${BACKEND_URL}/employee/upload-document-secure`, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(requirementName
          ? `Document uploaded and matched to "${requirementName}".`
          : 'Document uploaded securely!');
        setPendingRequirementUpload('');
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      alert("Failed to upload document: " + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleSecureDownload = async (file) => {
    if (!currentUser?.uid) {
      alert('You must be logged in to download documents.');
      return;
    }

    try {
      setDownloadingId(file.id);
      
      if (file.encrypted) {
        // Use secure download endpoint for encrypted files
        const downloadUrl = `${BACKEND_URL}/employee/download-document?` + new URLSearchParams({
          companyUID: companyId,
          employeeId: employeeId,
          documentId: file.id,
          userUID: currentUser?.uid || '',
          userEmail: currentUser?.email || ''
        });
        
        const response = await fetch(downloadUrl);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Download failed');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Legacy unencrypted file - use direct URL
        if (file.url) {
          window.open(file.url, '_blank');
        } else {
          throw new Error('No download URL available');
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download document: ' + (error.message || 'Unknown error'));
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) return <div>Loading documents...</div>;

  const uploadedFiles = Object.entries(documents.files || {})
    .filter(([, file]) => file && typeof file === 'object')
    .map(([id, file]) => ({ id, ...file }));
  
  // Merge explicit requirements with onboarding requirements, removing duplicates
  const explicitReqs = normalizeRequirementNames(documents.required || []);
  const allReqs = (() => {
    const combined = [...explicitReqs, ...onboardingReqs];
    const deduped = [];
    const seen = new Set();
    combined.forEach((name) => {
      const norm = normalizeText(name);
      if (!norm || seen.has(norm)) return;
      seen.add(norm);
      deduped.push(name);
    });
    return deduped;
  })();

  const resolveFileMatchedRequirement = (file) => (
    file?.requestedDocumentName ||
    file?.requiredDocumentName ||
    file?.matchedRequirement ||
    ''
  );

  const checkRequirementStatus = (reqName) => {
    const reqNorm = normalizeText(reqName);
    return uploadedFiles.find((file) => {
      const mappedName = resolveFileMatchedRequirement(file);
      if (normalizeText(mappedName) === reqNorm) return true;
      const fileName = String(file?.name || '');
      return normalizeText(fileName).includes(reqNorm);
    });
  };

  return (
    <div className="documents-container">
      <h1 className="page-title">My Documents</h1>
      <p className="page-subtitle">Manage your employment documents and records. All uploads are encrypted for your security.</p>

      {/* Required Documents Checklist */}
      <div className="section-card">
        <h2 className="section-title">Required Documents</h2>
        {allReqs.length === 0 ? (
          <p className="no-data">No specific documents required at this time.</p>
        ) : (
          <div className="checklist-grid">
            {allReqs.map((req, index) => {
                const matchedFile = checkRequirementStatus(req);
                const isUploaded = Boolean(matchedFile);
                return (
                    <div key={index} className={`checklist-item ${isUploaded ? 'uploaded' : 'missing'}`}>
                        <div className="status-icon">
                            {isUploaded ? (
                                <i className="fa-solid fa-check-circle"></i>
                            ) : (
                                <i className="fa-solid fa-circle-exclamation"></i>
                            )}
                        </div>
                        <div className="req-details">
                            <div className="req-name">{req}</div>
                            <div className="req-status">
                                {isUploaded ? `Matched: ${matchedFile?.name || 'File on record'}` : 'Action Required'}
                            </div>
                        </div>
                        {!isUploaded && (
                             <button
                               type="button"
                               className="upload-btn-mini"
                               onClick={() => {
                                 setPendingRequirementUpload(req);
                                 setSelectedRequirement(req);
                                 if (fileInputRef.current) fileInputRef.current.click();
                               }}
                               disabled={uploading}
                             >
                                <i className="fa-solid fa-upload"></i> Upload
                             </button>
                        )}
                    </div>
                )
            })}
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div className="section-card">
        <h2 className="section-title">Upload New Document</h2>
        {allReqs.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <label htmlFor="required-doc-select" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
              Match this upload to a requested document (optional)
            </label>
            <select
              id="required-doc-select"
              value={selectedRequirement}
              onChange={(e) => setSelectedRequirement(e.target.value)}
              disabled={uploading}
              style={{
                width: '100%',
                maxWidth: '420px',
                background: 'rgba(255,255,255,0.95)',
                color: '#031128',
                border: '1px solid rgba(86, 141, 237, 0.4)',
                borderRadius: '8px',
                padding: '10px 12px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px'
              }}
            >
              <option value="">No specific requirement</option>
              {allReqs.map((reqName) => (
                <option key={reqName} value={reqName}>{reqName}</option>
              ))}
            </select>
          </div>
        )}
        <div className="upload-area">
            <input 
                type="file" 
                id="file-upload" 
                ref={fileInputRef}
                onChange={handleFileUpload} 
                style={{ display: 'none' }} 
                disabled={uploading}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            <label htmlFor="file-upload" className="upload-button">
                <i className="fa-solid fa-cloud-arrow-up"></i>
                {uploading ? 'Uploading...' : (selectedRequirement ? `Upload for "${selectedRequirement}"` : 'Choose File to Upload')}
            </label>
            <div className="upload-hint">
                <i className="fa-solid fa-lock"></i> Files are encrypted before upload
            </div>
        </div>
      </div>

      {/* Uploaded Files List */}
      <div className="section-card">
        <h2 className="section-title">Uploaded Files</h2>
        {uploadedFiles.length === 0 ? (
          <p className="no-data">No documents uploaded yet.</p>
        ) : (
          <div className="files-grid">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="file-card">
                <div className="file-icon">
                    <i className={`fa-solid ${file.name?.endsWith('.pdf') ? 'fa-file-pdf' : 'fa-file-lines'}`}></i>
                </div>
                <div className="file-info">
                    <div className="file-name">{file.name}</div>
                    <div className="file-meta">
                        {new Date(file.uploadedAt).toLocaleDateString()} • {file.status || 'pending'}
                        {(file.requestedDocumentName || file.requiredDocumentName || file.matchedRequirement) && (
                          <span className="encrypted-badge" style={{ color: '#9bc3ff', background: 'rgba(86, 141, 237, 0.12)' }}>
                            <i className="fa-solid fa-link"></i>
                            {file.requestedDocumentName || file.requiredDocumentName || file.matchedRequirement}
                          </span>
                        )}
                        {file.encrypted && (
                          <span className="encrypted-badge">
                            <i className="fa-solid fa-lock"></i> Encrypted
                          </span>
                        )}
                    </div>
                </div>
                <button 
                  onClick={() => handleSecureDownload(file)}
                  className="file-action"
                  disabled={downloadingId === file.id}
                  title="Download"
                >
                    {downloadingId === file.id ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa-solid fa-download"></i>
                    )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .documents-container {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }
        .page-title {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            font-family: 'Montserrat', sans-serif;
        }
        .page-subtitle {
            color: rgba(255,255,255,0.7);
            margin-bottom: 32px;
            font-family: 'Montserrat', sans-serif;
        }
        .section-card {
            background: rgba(86, 141, 237, 0.08);
            border: 1px solid rgba(86, 141, 237, 0.15);
            border-radius: 12px;
            padding: 24px;
            margin-bottom: 24px;
        }
        .section-title {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 20px;
            color: #fff;
            font-family: 'Montserrat', sans-serif;
        }
        .no-data {
            color: rgba(255,255,255,0.5);
            font-style: italic;
        }
        
        /* Checklist Styling */
        .checklist-grid {
            display: grid;
            gap: 12px;
        }
        .checklist-item {
            display: flex;
            align-items: center;
            padding: 16px;
            border-radius: 8px;
            background: rgba(0,0,0,0.2);
            border: 1px solid transparent;
            transition: all 0.2s;
        }
        .checklist-item.missing {
            border-left: 4px solid #ff4444;
            background: rgba(255, 68, 68, 0.05);
        }
        .checklist-item.uploaded {
            border-left: 4px solid #00C851;
            background: rgba(0, 200, 81, 0.05);
        }
        .status-icon {
            font-size: 20px;
            margin-right: 16px;
        }
        .checklist-item.missing .status-icon { color: #ff4444; }
        .checklist-item.uploaded .status-icon { color: #00C851; }
        
        .req-details { flex: 1; }
        .req-name {
            font-weight: 600;
            font-size: 16px;
            margin-bottom: 4px;
        }
        .req-status {
            font-size: 12px;
            color: rgba(255,255,255,0.6);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .upload-btn-mini {
            background: rgba(86, 141, 237, 0.2);
            color: #568ded;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            gap: 6px;
            border: none;
        }
        .upload-btn-mini:hover {
            background: #568ded;
            color: white;
        }

        /* Upload Area */
        .upload-area {
            border: 2px dashed rgba(86, 141, 237, 0.3);
            border-radius: 12px;
            padding: 40px;
            text-align: center;
            transition: all 0.2s;
        }
        .upload-area:hover {
            border-color: #568ded;
            background: rgba(86, 141, 237, 0.05);
        }
        .upload-button {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            background: #568ded;
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            transition: background 0.2s;
        }
        .upload-button:hover {
            background: #4a7bc7;
        }
        .upload-hint {
            margin-top: 12px;
            font-size: 12px;
            color: #40EB6E;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
        }

        /* Files Grid */
        .files-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 16px;
        }
        .file-card {
            background: rgba(0,0,0,0.2);
            border: 1px solid rgba(255,255,255,0.05);
            border-radius: 8px;
            padding: 16px;
            display: flex;
            align-items: center;
            gap: 12px;
            transition: transform 0.2s;
        }
        .file-card:hover {
            transform: translateY(-2px);
            background: rgba(0,0,0,0.3);
        }
        .file-icon {
            font-size: 24px;
            color: #568ded;
            background: rgba(86, 141, 237, 0.1);
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
        }
        .file-info {
            flex: 1;
            overflow: hidden;
        }
        .file-name {
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 4px;
            font-size: 14px;
        }
        .file-meta {
            font-size: 11px;
            color: rgba(255,255,255,0.5);
            text-transform: capitalize;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
        }
        .encrypted-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            color: #40EB6E;
            font-size: 10px;
            padding: 2px 6px;
            background: rgba(64, 235, 110, 0.1);
            border-radius: 4px;
        }
        .file-action {
            color: rgba(255,255,255,0.4);
            transition: color 0.2s;
            padding: 8px;
            background: none;
            border: none;
            cursor: pointer;
            font-size: 16px;
        }
        .file-action:hover:not(:disabled) {
            color: #fff;
        }
        .file-action:disabled {
            cursor: wait;
            opacity: 0.5;
        }
      `}</style>
    </div>
  );
};

export default Documents;
